import { type EventCatalogEntry, lookup } from "@repo/vihangam-yog-shared";

import { ZOOM_ROOM } from "./activities";

/**
 * Pure, timezone-safe calendar math for `CalendarMonth` (`../ui/CalendarMonth.tsx`).
 * Nothing here reads `new Date()` or parses an ambiguous date string - every
 * function is given explicit Y/M/D numbers (or an unambiguous ISO instant,
 * read back through a fixed IANA zone) so the result never shifts with the
 * host machine's own clock or timezone.
 */

export interface YearMonth {
  /** Full year, e.g. 2026. */
  year: number;
  /** 1-12. */
  month: number;
}

/** One cell of a Monday-first month grid. */
export interface CalendarDay {
  year: number;
  /** 1-12. */
  month: number;
  day: number;
  /** False for a leading/trailing day borrowed from an adjacent month. */
  inMonth: boolean;
}

/** A catalog event placed on one calendar date. */
export interface PlacedEvent {
  title: string;
  timeLabel: string;
  href: string;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/** "October 2026" for a given year+month. */
export function monthLabel(year: number, month: number): string {
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

/**
 * The UTC weekday (0=Sunday..6=Saturday) of an explicit Y/M/D. Built with
 * `Date.UTC` from plain numbers only, then read back with the UTC getter -
 * never the local-timezone `new Date(string)` + `getDay()` pattern that
 * shifts a day depending on the host's own clock.
 */
function utcWeekday(year: number, month: number, day: number): number {
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

/** Days in a given month (`Date.UTC`'s day-0-of-next-month rolls back to the last day of this one). */
// fallow-ignore-next-line unused-export -- exercised directly by its unit test
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** How many adjacent-month days lead a Monday-first grid before day 1. */
function leadingDayCount(year: number, month: number): number {
  const sundayFirst = utcWeekday(year, month, 1);
  return (sundayFirst + 6) % 7;
}

/**
 * The Monday-first grid of day cells for a year+month, as whole weeks:
 * leading days borrow from the previous month and trailing days from the
 * next so every row has 7 cells. `Date.UTC` normalises the out-of-range
 * day numbers used to compute the adjacent-month cells, including year
 * rollover at a December/January boundary.
 */
export function monthGrid(year: number, month: number): CalendarDay[][] {
  const leading = leadingDayCount(year, month);
  const total = daysInMonth(year, month);
  const trailing = (7 - ((leading + total) % 7)) % 7;
  const cells: CalendarDay[] = [];
  for (let i = 0; i < leading + total + trailing; i++) {
    const dayOfTargetMonth = i - leading + 1;
    const d = new Date(Date.UTC(year, month - 1, dayOfTargetMonth));
    cells.push({
      year: d.getUTCFullYear(),
      month: d.getUTCMonth() + 1,
      day: d.getUTCDate(),
      inMonth: dayOfTargetMonth >= 1 && dayOfTargetMonth <= total,
    });
  }
  const weeks: CalendarDay[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  return weeks;
}

/** Day-of-month numbers in year+month that fall on `weekday` (0=Sunday..6=Saturday). */
// fallow-ignore-next-line unused-export -- exercised directly by its unit test
export function daysOnWeekday(
  year: number,
  month: number,
  weekday: number,
): number[] {
  const total = daysInMonth(year, month);
  const days: number[] = [];
  for (let day = 1; day <= total; day++) {
    if (utcWeekday(year, month, day) === weekday) {
      days.push(day);
    }
  }
  return days;
}

/** The month before, rolling the year back at a January boundary. */
export function prevMonth({ year, month }: YearMonth): YearMonth {
  return month === 1
    ? { year: year - 1, month: 12 }
    : { year, month: month - 1 };
}

/** The month after, rolling the year forward at a December boundary. */
export function nextMonth({ year, month }: YearMonth): YearMonth {
  return month === 12
    ? { year: year + 1, month: 1 }
    : { year, month: month + 1 };
}

const WEEKDAY_BY_NAME = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
} satisfies Readonly<Record<string, number>>;

/** Parses the catalog's recurring `dateLabel` convention, "Every <Weekday>". */
function everyWeekday(dateLabel: string): number | undefined {
  const match = /^Every (\w+)$/.exec(dateLabel);
  return match ? lookup(WEEKDAY_BY_NAME, match[1].toLowerCase()) : undefined;
}

/** Singapore chapter's own zone - the SG site's calendar is always read in it. */
const SG_TIME_ZONE = "Asia/Singapore";

/** A calendar date resolved in a fixed zone - `YearMonth` plus the day. */
interface ZonedDate extends YearMonth {
  day: number;
}

/**
 * The Y/M/D an ISO instant falls on in `timeZone`, via `Intl` (never the
 * host machine's own zone). The same approach the shared package's own
 * `localDateKey` uses for the other chapters' calendars.
 */
function zonedYearMonthDay(iso: string, timeZone: string): ZonedDate {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(iso));
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? "0");
  return { year: get("year"), month: get("month"), day: get("day") };
}

/**
 * The catalog events to show on each date of a year+month: recurring rows
 * (no `startsAt`, dateLabel "Every <Weekday>") repeat on every matching
 * weekday in the month and open the shared Zoom room - the catalog has no
 * per-event Zoom field yet, so that shared room is the fallback the brief
 * asks for, used unconditionally today. Dated rows (`startsAt` set)
 * appear once, on their Singapore-time calendar date, and open the single
 * `/register` form both October events share. Returns day-of-month ->
 * events, scoped to this month only - a caller must still skip the map
 * for an adjacent-month grid cell (`CalendarDay.inMonth`).
 */
export function eventsForMonth(
  year: number,
  month: number,
  catalog: readonly EventCatalogEntry[],
): Map<number, PlacedEvent[]> {
  const byDay = new Map<number, PlacedEvent[]>();
  const place = (day: number, event: PlacedEvent) => {
    const existing = byDay.get(day);
    if (existing) {
      existing.push(event);
    } else {
      byDay.set(day, [event]);
    }
  };

  for (const entry of catalog) {
    if (entry.startsAt) {
      const at = zonedYearMonthDay(entry.startsAt, SG_TIME_ZONE);
      if (at.year === year && at.month === month) {
        place(at.day, {
          title: entry.title,
          timeLabel: entry.timeLabel ?? "",
          href: "/register",
        });
      }
      continue;
    }
    const weekday = everyWeekday(entry.dateLabel);
    if (weekday === undefined) {
      continue;
    }
    for (const day of daysOnWeekday(year, month, weekday)) {
      place(day, {
        title: entry.title,
        timeLabel: entry.timeLabel ?? "",
        href: ZOOM_ROOM.href,
      });
    }
  }
  return byDay;
}
